import { useQuery } from "@tanstack/react-query";
import { TIER_NAMES } from "../../lib/api";
import { ApiError, doorSettingsQuery, meQuery, planQuery } from "../../lib/queries";
import { useThemeChoice } from "../../lib/theme";
import { AccountPage, DoorPage } from "../you/door-page";
import { HELP_LABEL, HelpPage } from "../you/help-page";
import { REOPEN_QUESTION } from "../you/reopen";
import { NOB_SWITCH_LABEL, ThemeSwitcher } from "../you/theme-switcher";
import { whoami } from "../ui/account";
import { NobFace } from "./mascot";
import { Group, NavRow, SidePanel } from "./surfaces";
import { useShellUi, type SettingsFocus, type SettingsPage } from "./state";

export const SETTINGS_LABEL = "Settings";
export const RECIPIENTS_FREE =
  "Recipients are free forever. A high standing never guarantees a reply.";

const TITLE: Record<SettingsPage, string> = {
  home: "Settings",
  look: "Look",
  door: "Your door",
  account: "Account",
  help: HELP_LABEL,
};

/**
 * Everything about you, as a panel rather than as rooms.
 *
 * FOUR PAGES (wave 14a added "Get help"). "Channels" stood here too and led to a
 * screen offering to connect the doorman to six chat platforms. The port behind
 * it is mocked on every deployed environment, so the screen connected to
 * nothing and said "connected" about it (ruling R-0908-3).
 *
 * RULING R-0919-1 MADE THAT PERMANENT UNTIL THE PAPERWORK EXISTS. Privacy 2.4
 * stopped describing linked chat channels at all — a policy may not describe
 * processing that does not occur — so there must be no way to start one, and
 * the api now refuses to boot in production with that port real until a Terms
 * clause, a Privacy §6.1/§11 sentence and Meta in the vendor register exist.
 * The server, its port and `docs/channels.md` are untouched; this row is what
 * comes back first, and `lib/channels-dark.test.ts` is the pin on the whole
 * bundle that stops it coming back as some other screen meanwhile.
 *
 * The rooms it replaces (`/you`, `/rules`) were addresses
 * because they were places you went. Nothing here is a destination: it is the
 * back of the door, opened for a minute and shut again, and a shell that
 * navigated away from the conversation to change a theme would be losing the
 * reader's place to save them a slide-over.
 *
 * The index states the current answer beside every row, so the panel reads as
 * a summary of the whole account rather than as a menu somebody has to open
 * five times to learn what is set.
 */
export function SettingsPanel({
  page,
  focus,
}: {
  page: SettingsPage;
  /** Which block of the page the CTA that opened this actually meant. */
  focus?: SettingsFocus;
}) {
  const ui = useShellUi();
  return (
    /**
     * ONE WIDTH FOR EVERY PAGE. `wide` used to be turned on for the pages with
     * the most on them, which made the panel
     * change size as you walked into it, and a panel that resizes when you press
     * a row reads as a different panel arriving. The reference build is 380px
     * throughout, including for a door page carrying the whole rules editor; the
     * plates are what make that width work, and they are now here.
     */
    <SidePanel
      title={TITLE[page]}
      label={SETTINGS_LABEL}
      onBack={page === "home" ? null : () => ui.openSettings("home")}
      onClose={ui.closeOverlay}
    >
      {/* The panel's own gutter, above every page: the plates inset themselves
          horizontally, so what is owned here is the vertical breathing room. */}
      <div className="py-4">
        {page === "home" ? (
          <Home />
        ) : (
          /* A page deeper in slides in from the right, the direction the
             chevrons point. Keyed so that walking from one page to another
             re-runs it rather than swapping the contents of a page that has
             already arrived. */
          <div key={page} className="nob-settings-page">
            {page === "look" && <ThemeSwitcher />}
            {page === "door" && <DoorPage focus={focus} />}
            {page === "account" && <AccountPage />}
            {page === "help" && <HelpPage />}
          </div>
        )}
      </div>
    </SidePanel>
  );
}

function Home() {
  const ui = useShellUi();
  const { data: me } = useQuery(meQuery());
  const { data: settings, error: settingsError } = useQuery({
    ...doorSettingsQuery(),
    retry: false,
  });
  /**
   * THE GATE IS THE DOOR, NOT THE HANDLE (ruling R-0828-3, and C5's rule that a
   * knocker signing in must not get a door they did not ask for).
   *
   * These rows used to hang off `handle`, which was the same fact by accident:
   * only an email-lane account could be handle-less, and only a handle-less
   * account could be door-less. Every account holds an X handle now —
   * `users.x_handle` is NOT NULL — so the handle answers "is this a person",
   * and this answers "is there a door".
   *
   * ONLY A 404 HIDES THE ROWS, and the distinction is load-bearing: "there is
   * no door" is a fact the server stated, while a read that is still in flight
   * or that failed for some other reason is a fact nobody has. Hiding on the
   * second would take an owner's own settings away from them the moment the
   * network wobbled — so the rows stand, and the page behind them has its own
   * honest thing to say about a door it could not read.
   */
  const hasDoor = !(settingsError instanceof ApiError && settingsError.code === "DOOR_NOT_FOUND");
  const { data: plan } = useQuery(planQuery());
  const theme = useThemeChoice();

  return (
    <div>
      <button
        type="button"
        onClick={() => ui.openSettings(hasDoor ? "door" : "account")}
        className="mb-5 flex w-full flex-col items-center gap-2 px-3 pt-2 text-center
                   transition-opacity duration-75 hover:opacity-90 focus-visible:outline-none
                   focus-visible:ring-2 focus-visible:ring-focus/70"
      >
        <NobFace mood={settings?.status === "live" ? "open" : "watching"} size="xl" />
        <span className="text-sm font-semibold">{me?.displayName ?? "You"}</span>
        {me && <span className="text-[12px] text-muted">{whoami(me)}</span>}
      </button>

      <Group>
        <NavRow
          label="Look"
          value={theme === "nob" ? NOB_SWITCH_LABEL : theme === null ? "System" : capital(theme)}
          onClick={() => ui.openSettings("look")}
        />
        {/* THE STATE IS A CONTROL WHEN IT IS "PAUSED" (owner item 13). A live
            door has nothing to press — "Live" is the answer somebody wanted.
            A paused one is a state its owner may not have meant to still be in,
            and the way out of it was five sections down the page this row leads
            to; it is the word itself now. */}
        {hasDoor && (
          <NavRow
            label="Your door"
            value={settings ? (settings.status === "live" ? "Live" : "Paused") : undefined}
            onClick={() => ui.openSettings("door")}
            {...(settings?.status === "paused"
              ? { onValueClick: () => ui.openSheet("reopen"), valueAriaLabel: REOPEN_QUESTION }
              : {})}
          />
        )}
      </Group>

      {/* Money is a panel of its own, not a page of this one: it is the only
          subject here that a person arrives at from somewhere else (a paywall,
          a checkout coming back), and a view inside Settings could not be
          opened directly from either. The row said "Plan and credits" until
          ruling R-0916-1; the panel is the plan now, and nothing else. */}
      <Group>
        <NavRow
          label="Plan"
          value={plan ? TIER_NAMES[plan.plan.tier] : undefined}
          onClick={() => ui.openBilling("home")}
        />
      </Group>

      <Group>
        <NavRow label="Account" onClick={() => ui.openSettings("account")} />
        {/* Its own row under Account, and the last one: writing to us is the
            only thing in this panel that is not a setting. It is here rather
            than in a footer link because a person looking for help looks where
            the other things about them are. */}
        <NavRow label={HELP_LABEL} onClick={() => ui.openSettings("help")} />
      </Group>

      <p className="px-6 pt-2 pb-2 text-center text-[11px] leading-snug text-muted">
        {RECIPIENTS_FREE}
      </p>
    </div>
  );
}

function capital(word: string): string {
  return word.charAt(0).toUpperCase() + word.slice(1);
}
